import React, { Fragment, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

// Layouts
import AppLayout from 'layouts/AppLayout';
import EmptyLayout from 'layouts/EmptyLayout';

import { useSeller } from 'hooks';

// Components
import { LoadingScreen } from 'components/layouts';

// Containers
import GuestGuard from '../GuestGuard';
import PanelGuard from '../PanelGuard';
import { accountPanelRoutes } from './accounts';
// Routes
import {
  institutionChooseRoute,
  institutionPanelRoutes,
  institutionRegisterRoute,
} from './institutions';
import { sellerChooseRoute, sellerPanelRoutes, sellerRegisterRoute } from './sellers';

const routesConfig = [
  {
    exact: true,
    path: '/',
    component: () => <Redirect to="/accounts" />,
  },
  {
    exact: true,
    path: '/login',
    guard: GuestGuard,
    layout: EmptyLayout,
    component: lazy(() => import('pages/root/LoginPage')),
  },
  {
    exact: true,
    path: '/logout',
    layout: EmptyLayout,
    component: lazy(() => import('pages/root/LogoutPage')),
  },
  {
    path: '/sign-up',
    layout: EmptyLayout,
    guard: GuestGuard,
    routes: [
      {
        exact: true,
        path: '/sign-up/express',
        component: lazy(() => import('pages/root/ExpressSignUpPage')),
      },
      {
        exact: true,
        path: '/sign-up',
        component: lazy(() => import('pages/root/SignUpPage')),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    exact: true,
    path: '/password-reset/:uidb64/:token',
    layout: EmptyLayout,
    component: lazy(() => import('pages/root/PasswordConfirmPage')),
  },
  {
    exact: true,
    path: '/password-reset',
    layout: EmptyLayout,
    component: lazy(() => import('pages/root/PasswordResetPage')),
  },
  {
    exact: true,
    path: '/email-confirm/:uidb64/:token',
    layout: EmptyLayout,
    component: lazy(() => import('pages/root/EmailConfirmPage')),
  },
  {
    exact: true,
    path: '/not-authorized',
    layout: EmptyLayout,
    component: lazy(() => import('pages/root/NotAuthorizedPage')),
  },
  sellerRegisterRoute,
  institutionRegisterRoute,
  {
    // Single route for the three logged-in contexts so the shared AppLayout
    // (TopBar/NavBar chrome) persists across context switches instead of
    path: ['/accounts', '/sellers/:sellerSlug', '/institutions/:institutionSlug'],
    guard: PanelGuard,
    layout: AppLayout,
    routes: [
      ...accountPanelRoutes,
      ...sellerPanelRoutes,
      ...institutionPanelRoutes,
      { component: () => <Redirect to="/404" /> },
    ],
  },
  sellerChooseRoute,
  institutionChooseRoute,
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('pages/root/NotFoundPage')),
  },
  {
    path: '*',
    routes: [
      { exact: true, path: '/terms', component: () => <Redirect to="/terms" /> },
      { exact: true, path: '/privacy', component: () => <Redirect to="/privacy" /> },
      { exact: true, path: '/contact-us', component: () => <Redirect to="/contact" /> },
      { component: () => <Redirect to="/404" /> },
    ],
  },
];

const renderRoutes = (routes, permissions) =>
  routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, index) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;

          const isProtected =
            !!route.permissionKey &&
            Array.isArray(permissions) &&
            !permissions.includes(route.permissionKey);

          const Component = !isProtected
            ? route.component
            : () => <Redirect to="/not-authorized" />;

          return (
            <Route
              key={route.path || `${index}`}
              path={route.path}
              exact={route.exact}
              strict={route.strict}
              render={props => (
                <Guard>
                  <Layout>
                    {route.routes ? (
                      renderRoutes(route.routes, permissions)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;

function Routes() {
  const { sellerPermissions } = useSeller();

  return renderRoutes(routesConfig, sellerPermissions);
}

export default Routes;
